import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import LinearProgress from '@mui/material/LinearProgress'
import Typography from '@mui/material/Typography'

import { CSVUser, IMPORT_STEPS, ImportStep } from './ImportUsersCSVButton.helpers'

export interface DialogContentProps {
  step: ImportStep
  parsedUsers: CSVUser[]
  newUsers: CSVUser[]
  duplicateUsers: CSVUser[]
  invalidUsers: CSVUser[]
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void
}

const UsersList = ({
  users,
  title,
  color,
  listItemStyle = {}
}: {
  users: CSVUser[]
  title: string
  color?: string
  listItemStyle?: React.CSSProperties
}) => (
  <Box style={{ marginBottom: 16 }}>
    <Typography variant="body2" style={{ color, marginBottom: 8 }}>
      {title}
    </Typography>
    <Box component="ul" style={{ padding: '0 0 0 20px', margin: 0 }}>
      {users.map((user) => (
        <li key={user.email} style={listItemStyle}>
          <Typography variant="body2" style={{ color }}>
            {user.name} ({user.email})
          </Typography>
        </li>
      ))}
    </Box>
  </Box>
)

const UploadStep = ({
  onFileUpload
}: {
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void
}) => (
  <Box>
    <Typography variant="body1" gutterBottom>
      Upload a CSV file containing user information with the following columns:
    </Typography>
    <Box component="ul">
      <li>
        <strong>name</strong> - The user's full name
      </li>
      <li>
        <strong>email</strong> - The user's email address
      </li>
    </Box>

    <Button variant="contained" color="primary" component="label" size="small" sx={{ my: 2 }}>
      Select CSV File
      <input type="file" accept=".csv" onChange={onFileUpload} hidden />
    </Button>
  </Box>
)

const PreviewStep = ({
  parsedUsers,
  newUsers,
  duplicateUsers,
  invalidUsers,
  onFileUpload
}: {
  parsedUsers: CSVUser[]
  newUsers: CSVUser[]
  duplicateUsers: CSVUser[]
  invalidUsers: CSVUser[]
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void
}) => (
  <div>
    <Typography variant="h6" gutterBottom>
      Upload Preview
    </Typography>

    <Typography variant="body2" gutterBottom>
      Total parsed: {parsedUsers.length} users
    </Typography>

    {newUsers.length > 0 && (
      <UsersList
        users={newUsers}
        title={`✓ ${newUsers.length} new user(s) will be created`}
        color="green"
      />
    )}

    {duplicateUsers.length > 0 && (
      <UsersList
        users={duplicateUsers}
        title={`⚠ ${duplicateUsers.length} user(s) already exist and will be skipped`}
        color="orange"
      />
    )}

    {invalidUsers.length > 0 && (
      <UsersList
        users={invalidUsers}
        title={`✗ ${invalidUsers.length} invalid user(s) will be skipped`}
        color="red"
      />
    )}

    <Button variant="contained" color="primary" component="label" size="small" sx={{ my: 2 }}>
      Select CSV File
      <input type="file" accept=".csv" onChange={onFileUpload} hidden />
    </Button>
  </div>
)

const UploadingStep = ({ newUsers }: { newUsers: CSVUser[] }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
    <Typography
      variant="h5"
      gutterBottom
      sx={{
        fontWeight: 600,
        color: (theme) => theme.palette.primary.main,
        textAlign: 'center',
        marginBottom: 1
      }}
    >
      Uploading Users...
    </Typography>

    <Typography
      variant="body1"
      gutterBottom
      sx={{
        textAlign: 'center',
        color: (theme) => theme.palette.text.secondary,
        marginBottom: 3
      }}
    >
      Processing {newUsers.length} user{newUsers.length !== 1 ? 's' : ''}
    </Typography>

    <Box sx={{ width: '100%', maxWidth: 300 }}>
      <LinearProgress
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: (theme) => theme.palette.grey[200],
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            background: (theme) =>
              `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`
          }
        }}
      />
    </Box>

    <Typography
      variant="caption"
      sx={{
        marginTop: 2,
        color: (theme) => theme.palette.text.disabled,
        fontStyle: 'italic'
      }}
    >
      Please wait while we create the accounts...
    </Typography>
  </Box>
)

export const DialogContent = ({
  step,
  parsedUsers,
  newUsers,
  duplicateUsers,
  invalidUsers,
  onFileUpload
}: DialogContentProps) => {
  switch (step) {
    case IMPORT_STEPS.UPLOAD:
      return <UploadStep onFileUpload={onFileUpload} />
    case IMPORT_STEPS.PREVIEW:
      return (
        <PreviewStep
          parsedUsers={parsedUsers}
          newUsers={newUsers}
          duplicateUsers={duplicateUsers}
          invalidUsers={invalidUsers}
          onFileUpload={onFileUpload}
        />
      )
    case IMPORT_STEPS.UPLOADING:
      return <UploadingStep newUsers={newUsers} />
    default:
      return <UploadStep onFileUpload={onFileUpload} />
  }
}
